import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, FileSignature, Ban, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const fetcher = (url) => api.get(url).then((r) => r.data);

const initialForm = {
  unit_id: "",
  tenant_id: "",
  start_date: todayISO(),
  end_date: "",
  monthly_rent: 0,
  deposit: 0,
  billing_day: 1,
  late_fee_type: "fixed_daily",
  late_fee_value: 10000,
  notes: "",
};

export default function Contracts() {
  const { data: contracts = [], isLoading } = useSWR("/contracts", fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const { data: tenants = [] } = useSWR("/tenants", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);

  const openNew = () => {
    setForm(initialForm);
    setOpen(true);
  };

  const onUnitChange = (id) => {
    const unit = unitMap[id];
    setForm({ ...form, unit_id: id, monthly_rent: unit?.rent_amount || form.monthly_rent });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.unit_id || !form.tenant_id || !form.end_date) {
      toast.error("Lengkapi unit, penyewa, dan tanggal.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        monthly_rent: Number(form.monthly_rent),
        deposit: Number(form.deposit),
        billing_day: Number(form.billing_day),
        late_fee_value: Number(form.late_fee_value),
      };
      const { data } = await api.post("/contracts", body);
      toast.success(`Kontrak dibuat · ${data.bills_generated || 0} tagihan digenerate`);
      setOpen(false);
      mutate("/contracts");
      mutate("/units");
      mutate((k) => typeof k === "string" && k.startsWith("/bills"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const endContract = async (c) => {
    if (!confirm(`Akhiri kontrak untuk unit ${unitMap[c.unit_id]?.code || ""}?`)) return;
    try {
      await api.post(`/contracts/${c.id}/end`);
      toast.success("Kontrak diakhiri");
      mutate("/contracts");
      mutate("/units");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (c) => {
    if (!confirm("Hapus kontrak beserta tagihannya?")) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Kontrak dihapus");
      mutate("/contracts");
      mutate("/units");
      mutate((k) => typeof k === "string" && k.startsWith("/bills"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const statusTone = (s) =>
    s === "active" ? "bg-success/15 text-success" :
    s === "ended" ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground";

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Operasional
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Kontrak Sewa</h1>
          <p className="text-sm text-muted-foreground mt-1">Buat kontrak baru — tagihan bulanan otomatis di-generate.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-contract-button">
          <Plus className="h-4 w-4" /> Buat Kontrak
        </Button>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : contracts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <FileSignature className="h-7 w-7" />
            </div>
            <div>
              <div className="font-display text-lg font-medium">Belum ada kontrak</div>
              <p className="text-sm text-muted-foreground mt-1">Buat kontrak pertama untuk mulai mencatat sewa.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contracts.map((c) => (
            <Card key={c.id} data-testid={`contract-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-md font-mono">{unitMap[c.unit_id]?.code || "?"}</Badge>
                      <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${statusTone(c.status)}`}>{c.status}</Badge>
                    </div>
                    <div className="mt-2 font-medium truncate">{tenantMap[c.tenant_id]?.name || "Penyewa tidak ditemukan"}</div>
                    <div className="text-xs text-muted-foreground truncate">{unitMap[c.unit_id]?.name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sewa/bln</div>
                    <div className="font-display text-lg font-medium tracking-tight">{formatIDR(c.monthly_rent)}</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Mulai</div>
                    <div>{formatDate(c.start_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Berakhir</div>
                    <div>{formatDate(c.end_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Deposit</div>
                    <div>{formatIDR(c.deposit)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Denda</div>
                    <div>
                      {c.late_fee_type === "fixed_daily"
                        ? `${formatIDR(c.late_fee_value)} / hari`
                        : c.late_fee_type === "percentage_daily"
                        ? `${c.late_fee_value}% / hari`
                        : "—"}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/contracts/${c.id}/print`)}
                    className="gap-1"
                    data-testid={`print-contract-${c.id}`}
                  >
                    <Printer className="h-3.5 w-3.5" /> Cetak
                  </Button>
                  {c.status === "active" && (
                    <Button variant="outline" size="sm" onClick={() => endContract(c)} className="gap-1" data-testid={`end-contract-${c.id}`}>
                      <Ban className="h-3.5 w-3.5" /> Akhiri
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => del(c)} className="gap-1 text-destructive hover:text-destructive" data-testid={`delete-contract-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Hapus
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buat Kontrak Sewa</DialogTitle>
            <DialogDescription>Tagihan bulanan akan otomatis di-generate untuk seluruh periode kontrak.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={form.unit_id} onValueChange={onUnitChange}>
                  <SelectTrigger data-testid="contract-unit-select"><SelectValue placeholder="Pilih unit" /></SelectTrigger>
                  <SelectContent>
                    {units.filter((u) => u.status !== "maintenance").map((u) => (
                      <SelectItem key={u.id} value={u.id} disabled={u.status === "terisi"}>
                        {u.code} — {u.name} {u.status === "terisi" ? "(terisi)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Penyewa</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="contract-tenant-select"><SelectValue placeholder="Pilih penyewa" /></SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tanggal Mulai</Label>
                <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="contract-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Tanggal Berakhir</Label>
                <Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-end-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Sewa Bulanan (Rp)</Label>
                <Input type="number" min="0" required value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="contract-rent-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Deposit (Rp)</Label>
                <Input type="number" min="0" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Tgl. Tagih</Label>
                <Input type="number" min="1" max="28" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Tipe Denda</Label>
                <Select value={form.late_fee_type} onValueChange={(v) => setForm({ ...form, late_fee_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed_daily">Nominal tetap / hari (Rp)</SelectItem>
                    <SelectItem value="percentage_daily">Persentase / hari (%)</SelectItem>
                    <SelectItem value="none">Tanpa denda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.late_fee_type !== "none" && (
              <div className="space-y-1.5">
                <Label>Nilai Denda ({form.late_fee_type === "fixed_daily" ? "Rp/hari" : "% per hari dari tagihan"})</Label>
                <Input type="number" min="0" step="0.01" value={form.late_fee_value} onChange={(e) => setForm({ ...form, late_fee_value: e.target.value })} data-testid="contract-late-fee-value" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="contract-save-button">
                {saving ? "Menyimpan..." : "Simpan & Generate Tagihan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
